import { enquiryDraft } from '@/lib/enquiry';
import React, { useRef, useState } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Mail, Phone, MapPin, ArrowRight } from 'lucide-react';

const infoCards = [
  {
    icon: Mail,
    label: 'Email Us',
    value: 'info@renaissancegardenschools.com',
    href: 'mailto:info@renaissancegardenschools.com',
  },
  {
    icon: Phone,
    label: 'Call Us',
    value: '08051283366',
    href: 'tel:08051283366',
  },
  {
    icon: MapPin,
    label: 'Visit Us',
    value: '35 Otunba Akin Joacquin Street, Ago Palace Way, Okota, Lagos State',
    href: 'https://maps.google.com/?q=35+Otunba+Akin+Joacquin+Street+Okota+Lagos',
  },
];

/* ── Floating info card ── */
interface InfoCardProps {
  card: typeof infoCards[number];
  index: number;
}

const InfoCard = ({ card, index }: InfoCardProps) => {
  const ref = useRef<HTMLAnchorElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-50px 0px' });

  return (
    <motion.a
      ref={ref}
      href={card.href}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -5, scale: 1.02 }}
      className="group flex items-start gap-4 p-6 rounded-2xl bg-white border border-zinc-100 shadow-[0_2px_20px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_40px_rgba(249,115,22,0.12)] hover:border-primary/20 transition-all"
    >
      <div className="w-11 h-11 rounded-xl bg-primary/8 flex items-center justify-center flex-shrink-0 group-hover:bg-primary group-hover:shadow-[0_0_20px_rgba(249,115,22,0.35)] transition-all duration-400">
        <card.icon size={20} className="text-primary group-hover:text-white transition-colors duration-400" />
      </div>
      <div className="min-w-0">
        <p className="text-[9px] font-black uppercase tracking-[0.28em] text-zinc-400 mb-1">
          {card.label}
        </p>
        <p className="font-bold text-zinc-800 text-sm leading-snug break-words">{card.value}</p>
      </div>
    </motion.a>
  );
};

/* ── Contact form ── */
const ContactForm = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px 0px' });

  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: 40 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
      className="relative bg-white rounded-[2rem] sm:rounded-[2.5rem] p-6 sm:p-10 md:p-14 border border-zinc-100 shadow-[0_20px_80px_rgba(0,0,0,0.08)] overflow-hidden"
    >
      {/* Accent blob */}
      <div className="absolute -top-16 -right-16 w-48 h-48 bg-primary/8 rounded-full blur-3xl" />

      <div className="relative z-10">
        <h3 className="font-display font-black text-[clamp(1.8rem,4vw,2.8rem)] text-zinc-900 tracking-tighter mb-2">
          Send an <span className="text-primary">Enquiry</span>
        </h3>
        <p className="text-zinc-500 font-medium text-[15px] mb-10">
          We'll get back to you within 24 hours.
        </p>

        {submitted ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center justify-center py-16 text-center gap-4"
          >
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-2">
              <Mail size={28} className="text-primary" />
            </div>
            <h4 className="font-black text-2xl text-zinc-900 tracking-tight">Your email draft is ready</h4>
            <p className="text-zinc-500 font-medium max-w-xs">
              Send your prepared message from your email app to complete your enquiry.
            </p>
            <a href={enquiryDraft(form)} className="inline-flex justify-center rounded-xl bg-[#101c32] px-6 py-3 text-white font-semibold">Open email app</a>
            <button
              onClick={() => setSubmitted(false)}
              className="mt-4 text-primary text-sm font-bold hover:underline"
            >
              Edit message
            </button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <p className="text-sm text-slate-600">We’ll prepare an email draft for you to send to the school.</p>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full Name" name="name" value={form.name} onChange={handleChange} placeholder="Your name" />
              <Field label="Email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="hello@example.com" />
            </div>
            <Field label="Subject" name="subject" value={form.subject} onChange={handleChange} placeholder="Admission Enquiry" />
            <div className="space-y-1.5">
              <label htmlFor="enquiry-message" className="text-xs font-bold tracking-wide text-zinc-600 ml-1 block">
                Message
              </label>
              <textarea
                id="enquiry-message" name="message"
                rows={4}
                value={form.message}
                onChange={handleChange}
                required
                placeholder="Tell us what's on your mind..."
                className="w-full px-5 py-4 bg-zinc-50 border border-zinc-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/25 font-medium text-zinc-800 placeholder:text-zinc-500 resize-y transition-shadow text-base"
              />
            </div>

            <motion.button
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              className="w-full py-5 bg-zinc-900 text-white rounded-xl font-black text-base flex items-center justify-center gap-3 shadow-xl hover:bg-zinc-800 hover:shadow-2xl transition-all disabled:opacity-50"
            >
              Prepare Email
              <ArrowRight size={18} aria-hidden />
            </motion.button>
          </form>
        )}
      </div>
    </motion.div>
  );
};

/* ── Inline input field ── */
interface FieldProps {
  label: string;
  name: string;
  type?: string;
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  placeholder: string;
}

const Field = ({ label, name, type = 'text', value, onChange, placeholder }: FieldProps) => (
  <div className="space-y-1.5">
    <label htmlFor={`enquiry-${name}`} className="text-xs font-bold tracking-wide text-zinc-600 ml-1 block">
      {label}
    </label>
    <input
      type={type}
      id={`enquiry-${name}`} name={name}
      value={value}
      onChange={onChange}
      required
      placeholder={placeholder}
      className="w-full px-5 py-4 bg-zinc-50 border border-zinc-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/25 font-medium text-zinc-800 placeholder:text-zinc-500 transition-shadow text-base"
    />
  </div>
);

/* ── Section heading ── */
const SectionHeading = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-60px 0px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 36 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="mb-10 sm:mb-12 text-center"
    >
      <p className="text-primary text-[11px] font-black tracking-[0.32em] uppercase mb-5">
        Get in Touch
      </p>
      <h2 className="font-display font-black text-[clamp(2.5rem,6vw,4.5rem)] text-zinc-900 tracking-tighter leading-[0.9] mb-6">
        Connection <span className="text-gradient">Garden</span>
      </h2>
      <p className="text-zinc-500 text-lg font-medium max-w-xl mx-auto leading-relaxed">
        Our doors and hearts are always open. Reach out for any enquiries or to schedule a visit.
      </p>
    </motion.div>
  );
};

/* ── Map ── */
const MapBlock = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-60px 0px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="h-[280px] rounded-2xl overflow-hidden border border-zinc-100 shadow-[0_4px_30px_rgba(0,0,0,0.07)]"
    >
      <iframe
        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3964.093!2d3.3072495!3d6.4985217!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNsKwMjknNTQuNyJOIDPCsDE4JzI2LjEiRQ!5e0!3m2!1sen!2sng!4v1234567890123"
        width="100%"
        height="100%"
        style={{ border: 0 }}
        allowFullScreen
        loading="lazy"
        title="School Location"
        className="grayscale hover:grayscale-0 transition-all duration-700"
      />
    </motion.div>
  );
};

const Contact = () => (
  <section id="contact" className="py-16 sm:py-20 lg:py-24 bg-[#f8f9fc] relative overflow-hidden">
    {/* Top divider */}
    <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-zinc-200 to-transparent" />

    <div className="container mx-auto px-6">
      <SectionHeading />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-14 xl:gap-20 items-start">
        {/* Left — info cards + map */}
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4">
            {infoCards.map((card, i) => (
              <InfoCard key={i} card={card} index={i} />
            ))}
          </div>
          <MapBlock />
        </div>

        {/* Right — form */}
        <ContactForm />
      </div>
    </div>
  </section>
);

export default Contact;
